// Loading / error / empty for a reference-data screen that reads the backend.
//
// ⚠️ THESE SCREENS FAIL LOUD, like /catalog and unlike /inventory's stock
// read. The distinction is which fact the screen is FOR.
//
//   /inventory  shows catalog rows and hangs quantities off them. A failed
//               stock read still leaves real products on screen, so the rows
//               stay and every quantity reads unreported.
//
//   /sourcing, Settings > Exchange Rates
//               THE RATES ARE THE SCREEN. There is nothing else on it. A
//               failure clears the rows, says why, and offers a Retry -- and
//               THE TABLE IS NOT RENDERED AT ALL, because an empty grid under
//               a filter bar reads as "nothing matched" rather than "we could
//               not ask".
//
// ⚠️ AND THERE IS NO FALLBACK TO A MOCK RATE, ever. That is worse here than
// anywhere else in the app: anixify.quote.line resolves an imported line's
// duty and conversion rate out of these same tables, so a mock rendered under
// a failed read would show a figure the pricer is demonstrably not using --
// which is precisely the disagreement these endpoints were built to end.

import { AlertCircle, RefreshCw } from "lucide-react";
import {
  semanticColors, radius, spacing, heights, typography, iconSize, iconStroke,
} from "@/styles/tokens";

export type LoadStatus = "loading" | "ok" | "error";

export function ReferenceLoading({ testId, label }: { testId: string; label: string }) {
  return (
    <div
      data-testid={testId}
      style={{
        padding: spacing[40],
        textAlign: "center",
        fontSize: typography.fontSize.base,
        color: semanticColors.textPlaceholder,
      }}
    >
      {label}
    </div>
  );
}

export function ReferenceError({
  testId, retryTestId, message, detail, onRetry,
}: {
  testId: string;
  retryTestId: string;
  /** What could not be loaded, in the reader's words. */
  message: string;
  /** The backend's own reason. Never swallowed: "something went wrong" tells
   *  a person nothing they can act on or report. */
  detail: string;
  onRetry: () => void;
}) {
  return (
    <div
      data-testid={testId}
      className="flex items-start justify-between"
      style={{
        padding: spacing[12],
        marginBottom: spacing[12],
        border: `1px solid ${semanticColors.destructiveBorder}`,
        backgroundColor: semanticColors.destructiveBg,
        borderRadius: radius.base,
        gap: spacing[12],
      }}
    >
      <div
        className="flex items-start"
        style={{ gap: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.destructive }}
      >
        <AlertCircle
          size={iconSize.md}
          strokeWidth={iconStroke.regular}
          style={{ flexShrink: 0, marginTop: spacing[2] }}
        />
        <span>{message} {detail}</span>
      </div>
      <button
        data-testid={retryTestId}
        onClick={onRetry}
        style={{
          flexShrink: 0,
          height: heights.control,
          padding: `0 ${spacing[12]}px`,
          backgroundColor: semanticColors.surface,
          color: semanticColors.textSecondary,
          border: `1px solid ${semanticColors.border}`,
          borderRadius: radius.base,
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.medium,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: spacing[6],
        }}
      >
        <RefreshCw size={iconSize.md} strokeWidth={iconStroke.regular} />
        Retry
      </button>
    </div>
  );
}

/** A successful read that returned nothing. A THIRD STATE, not an error --
 *  the same distinction /inventory draws between "we could not ask" and "we
 *  asked and were told nothing", worded so it cannot be mistaken for a
 *  failure. */
export function ReferenceEmpty({ testId, label }: { testId: string; label: string }) {
  return (
    <div
      data-testid={testId}
      style={{
        padding: spacing[40],
        textAlign: "center",
        border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base,
        backgroundColor: semanticColors.panelBg,
        fontSize: typography.fontSize.sm,
        color: semanticColors.textSecondary,
      }}
    >
      {label}
    </div>
  );
}
